import { ComponentType, FC, ReactNode } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "components/ui/Button";

// ============= Types =============
export type BadgeVariant = "green" | "orange" | "red" | "gray";

export interface InfoSectionConfig {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onClick?: () => void;
  showExternalLink?: boolean;
  disabled?: boolean;
  subValue?: {
    text: string;
    onClick?: () => void;
  };
}

export interface EntityDetailsHeaderConfig {
  icon: ReactNode;
  title: string;
  logoURL?: string
  onLogoClick?: () => void;
  subtitle: string;
  infoSections: InfoSectionConfig[];
  customAside?: JSX.Element;
  badge?: {
    label: string;
    variant: BadgeVariant;
    icon?: ComponentType<{ className?: string }>;
  };
  metadata?: {
    icon: ComponentType<{ className?: string }>;
    text: string;
  };
  titleAction?: {
    label?: string;
    icon?: ComponentType<{ className?: string }>;
    onClick: () => void;
  };
}

// ============= Badge Component =============
interface BadgeProps {
  label: string;
  variant?: BadgeVariant;
  icon?: ComponentType<{ className?: string }>;
}

const Badge: FC<BadgeProps> = ({ label, variant = "gray", icon: Icon }) => {
  const variantStyles = {
    green: "bg-green-100 text-green-800 border-green-200",
    orange: "bg-orange-100 text-orange-800 border-orange-200",
    red: "bg-red-100 text-red-800 border-red-200",
    gray: "bg-gray-100 text-gray-800 border-gray-200"
  };

  return (
    <div className={`inline-flex items-center px-3 py-1 rounded-full border ${variantStyles[variant]}`}>
      {Icon && <Icon className="w-3 h-3 mr-1" />}
      <span className="text-xs font-semibold">{label}</span>
    </div>
  );
};

// ============= InfoSection Component =============
interface InfoSectionProps {
  config: InfoSectionConfig;
}

const InfoSection: FC<InfoSectionProps> = ({ config }) => {
  const { icon: Icon, label, value, onClick, showExternalLink, disabled, subValue } = config;

  const isClickable = onClick && !disabled;

  return (
    <div className="flex flex-col items-start space-y-1">
      <div className="flex items-center space-x-1">
        <Icon className="w-3 h-3 text-gray-500" />
        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
          {label}
        </p>
      </div>

      <button
        onClick={onClick}
        disabled={disabled}
        className={`text-xs font-semibold text-left transition-all duration-200 flex items-center space-x-1 group ${isClickable
          ? "text-primary/80 hover:text-primary cursor-pointer"
          : "text-primary/80"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <span>{value}</span>
        {showExternalLink && (
          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </button>

      {subValue && (
        <div className="mt-1">
          <button
            onClick={subValue.onClick}
            className="text-xs text-primary/70 hover:text-primary transition-colors cursor-pointer flex items-center space-x-1 group"
          >
            <span>→ {subValue.text}</span>
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      )}
    </div>
  );
};

// ============= EntityDetailsHeader Component =============
interface EntityDetailsHeaderProps {
  config: EntityDetailsHeaderConfig;
}

const EntityDetailsHeader: FC<EntityDetailsHeaderProps> = ({ config }) => {
  const { icon, title, subtitle, infoSections, badge, metadata, logoURL, onLogoClick, customAside, titleAction } = config;

  const MetadataIcon = metadata?.icon;
  const TitleActionIcon = titleAction?.icon;

  return (
    <div className="bg-white px-6 py-4 relative">
      <div className="flex items-center justify-between w-full">
        {/* Section gauche - Informations principales */}
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            {logoURL ? (
              <button
                type="button"
                onClick={onLogoClick}
                disabled={!onLogoClick}
                className={`w-24 h-24 border border-gray-200 rounded-lg shadow-sm flex items-center justify-center p-2 bg-white relative ${onLogoClick ? "group cursor-pointer hover:shadow-md transition-shadow" : "cursor-default"}`}
                aria-label={onLogoClick ? "Modifier les logos" : undefined}
              >
                <img
                  src={logoURL}
                  alt="logo"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
                {onLogoClick && (
                  <span className="absolute inset-0 rounded-lg bg-black/40 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    Modifier
                  </span>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onLogoClick}
                disabled={!onLogoClick}
                className={`w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center ${onLogoClick ? "cursor-pointer hover:bg-primary/15 transition-colors" : "cursor-default"}`}
                aria-label={onLogoClick ? "Ajouter un logo" : undefined}
              >
                {icon}
              </button>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-bold text-gray-900">{title}</h1>
              {titleAction && TitleActionIcon && (
                <Button
                  color="primary"
                  round
                  onClick={titleAction.onClick}
                >
                  {
                    titleAction.icon &&
                    <TitleActionIcon className="w-3.5 h-3.5" />
                  }
                  {
                    titleAction.label &&
                    <span>{titleAction.label}</span>
                  }
                </Button>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-semibold text-primary">{subtitle}</span>
            </div>
          </div>
        </div>

        {customAside}
        {
          !customAside &&
          <div className="flex items-start space-x-10">

            {infoSections.map((section, index) => (
              <InfoSection key={index} config={section} />
            ))}

            {/* Section Metadata avec Badge */}
            {(metadata || badge) && (
              <div className="">
                {metadata && MetadataIcon && (
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <MetadataIcon className="w-3 h-3" />
                    <span>{metadata.text}</span>
                  </div>
                )}
                {badge && (
                  <div className="absolute bottom-4 right-6">
                    <Badge
                      label={badge.label}
                      variant={badge.variant}
                      icon={badge.icon}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        }
      </div>
    </div>
  );
};

// ============= Export =============
export { EntityDetailsHeader, InfoSection, Badge };
